// Agendamiento · Profesionales y boxes
//
// Quién atiende cada servicio y cuándo: horario semanal por tramos (en múltiplos de
// 15 min) y bloqueos puntuales (vacaciones, licencias, feriados).
//
// Cambiar el horario o bloquear un día NO mueve ni cancela reservas pagadas. El
// backend devuelve las que quedaron afectadas y acá se muestran para reprogramarlas.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, DatePicker, Dropdown, Form, Input, Modal, Segmented, Select, Switch, Table, Tooltip } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, EditOutlined, LoadingOutlined, StopOutlined } from '@ant-design/icons';
import {
  FiBox, FiCheckCircle, FiCopy, FiInfo, FiLayers, FiMoreVertical, FiPlus, FiSearch, FiSlash, FiTrash2, FiUser,
} from 'react-icons/fi';
import Agenda from '../../../../service/Agenda';
import Recintos from '../../../../service/Recintos';
import { mensajeDeError } from '../../../../service/client';
import { useNotify } from '../../../../providers/NotificationProvider';
import useAuthContext from '../../../../hooks/useAuthContext';
import { can } from '../../../../config/rbac';
import { formatearNombre } from '../../../../utils/texto';
import {
  EncabezadoAdmin, TarjetaResumen, ChipEstado, ChipNeutro, claseBtnPrimario, claseBtnSecundario,
} from '../../../Ui/Admin/PiezasAdmin';
import { BarraFiltros, Chip, Vacio, claseBtnKebab } from './piezas';
import { DIAS, OPCIONES_HORA, comoDiasChile, comoHoraChile, fechaHora, textoBloqueo } from './util';

const { RangePicker } = DatePicker;

const porDia = (disponibilidad = []) =>
  Object.fromEntries(DIAS.map(d => [d, disponibilidad.filter(v => v.dia === d).map(({ desde, hasta }) => ({ desde, hasta }))]));
const aLista = (mapa) => DIAS.flatMap(d => (mapa[d] || []).filter(t => t.desde && t.hasta).map(t => ({ dia: d, ...t })));
const bloqueosVigentes = (a) => (a.bloqueos || []).filter(b => new Date(b.hasta) > new Date())
  .sort((x, y) => new Date(x.desde) - new Date(y.desde));

const CORTO = { lunes: 'lun', martes: 'mar', 'miércoles': 'mié', jueves: 'jue', viernes: 'vie', 'sábado': 'sáb', domingo: 'dom' };

/** "lun–vie 09:00–13:00" · "sáb 10:00–12:00": agrupa los días seguidos con el mismo horario. */
const resumenHorario = (disponibilidad = []) => {
  const mapa = porDia(disponibilidad);
  const grupos = [];
  for (const d of DIAS) {
    const tramos = mapa[d].map(t => `${t.desde}–${t.hasta}`).join(', ');
    const ultimo = grupos[grupos.length - 1];
    if (tramos && ultimo?.tramos === tramos && DIAS.indexOf(ultimo.hasta) === DIAS.indexOf(d) - 1) ultimo.hasta = d;
    else if (tramos) grupos.push({ desde: d, hasta: d, tramos });
  }
  return grupos.map(g => `${CORTO[g.desde]}${g.hasta !== g.desde ? `–${CORTO[g.hasta]}` : ''} ${g.tramos}`);
};

/** Reservas afectadas por un bloqueo o un cambio de horario. */
const ListaCitas = ({ citas }) => (
  <ul className="m-0 mt-2 p-0 list-none space-y-1.5">
    {citas.map(c => (
      <li key={c.id} className="text-sm text-gray-700">
        <span className="font-semibold tabular-nums">{fechaHora(c.inicio)}</span> · {c.servicio} · {c.cliente} <code className="text-xs">{c.codigo}</code>
      </li>
    ))}
  </ul>
);

function EditorHorario({ value, onChange }) {
  const cambiar = (dia, i, campo, v) =>
    onChange({ ...value, [dia]: value[dia].map((t, j) => (j === i ? { ...t, [campo]: v } : t)) });
  const agregar = (dia) => onChange({ ...value, [dia]: [...value[dia], { desde: '09:00', hasta: '13:00' }] });
  const quitar = (dia, i) => onChange({ ...value, [dia]: value[dia].filter((_, j) => j !== i) });
  const copiarLunes = () => onChange({
    ...value, ...Object.fromEntries(['martes', 'miércoles', 'jueves', 'viernes'].map(d => [d, value.lunes.map(t => ({ ...t }))])),
  });

  return (
    <div className="rounded-2xl border border-gray-100 divide-y divide-gray-100">
      {DIAS.map(dia => (
        <div key={dia} className="flex flex-wrap items-center gap-2 px-3 py-2">
          <span className="w-24 text-sm font-semibold text-gray-700 capitalize">{dia}</span>
          {value[dia].length === 0 && <span className="text-sm text-gray-400">No atiende</span>}
          {value[dia].map((t, i) => (
            <span key={i} className="inline-flex items-center gap-1 pl-1 pr-1 py-0.5 rounded-full bg-gray-50 border border-gray-100">
              <Select size="small" variant="borderless" style={{ width: 78 }} value={t.desde} options={OPCIONES_HORA} onChange={(v) => cambiar(dia, i, 'desde', v)} aria-label={`Desde ${dia}`} />
              <span className="text-gray-400">–</span>
              <Select size="small" variant="borderless" style={{ width: 78 }} value={t.hasta} options={OPCIONES_HORA} onChange={(v) => cambiar(dia, i, 'hasta', v)} aria-label={`Hasta ${dia}`} />
              <button type="button" onClick={() => quitar(dia, i)} aria-label={`Quitar tramo del ${dia}`}
                className="w-7 h-7 inline-flex items-center justify-center rounded-full text-gray-400 hover:bg-red-50 hover:text-red-600">
                <FiTrash2 size={13} />
              </button>
            </span>
          ))}
          <button type="button" onClick={() => agregar(dia)} className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
            <FiPlus size={14} /> Tramo
          </button>
        </div>
      ))}
      <div className="px-3 py-2">
        <button type="button" onClick={copiarLunes} disabled={!value.lunes.length}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-primary disabled:opacity-40">
          <FiCopy size={14} /> Copiar el lunes de martes a viernes
        </button>
      </div>
    </div>
  );
}

/**
 * Crear o editar un profesional o box. Componente aparte a propósito: se monta en cada
 * apertura, así parte con su propio formulario y horario (con un único useForm
 * compartido, reabrir mostraba los datos del anterior).
 */
function ModalAgenda({ agenda, recintos, servicios, onCerrar, onGuardado }) {
  const notify = useNotify();
  const [form] = Form.useForm();
  const [guardando, setGuardando] = useState(false);
  const [horario, setHorario] = useState(() => porDia(agenda.disponibilidad));
  const valoresIniciales = agenda._id
    ? { nombre: agenda.nombre, tipo: agenda.tipo, recinto: agenda.recinto?._id || agenda.recinto, servicios: (agenda.servicios || []).map(s => s._id || s), activo: agenda.activo }
    : { tipo: 'profesional', activo: true, servicios: [] };

  const guardar = async (valores) => {
    setGuardando(true);
    try {
      const datos = { ...valores, disponibilidad: aLista(horario) };
      const r = agenda._id ? await Agenda.actualizarAgenda(agenda._id, datos) : await Agenda.crearAgenda(datos);
      onGuardado(r.advertencias);
    } catch (err) {
      notify.error(mensajeDeError(err, 'No se pudo guardar'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      open onCancel={onCerrar} width={760} destroyOnHidden
      title={agenda?._id ? <>Editar <span className="hl-amarillo">{agenda.nombre}</span></> : 'Nuevo profesional o box'}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCerrar} className={claseBtnSecundario}>Cancelar</button>
          <button type="button" onClick={() => form.submit()} disabled={guardando} className={claseBtnPrimario}>
            {guardando && <LoadingOutlined />} {agenda?._id ? 'Guardar cambios' : 'Crear'}
          </button>
        </div>
      }
    >
      <Form form={form} layout="vertical" onFinish={guardar} autoComplete="off" className="form-compacto" initialValues={valoresIniciales}>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_160px_220px] gap-x-4">
          <Form.Item name="nombre" label="Nombre" rules={[{ required: true, message: 'Indica el nombre' }]}>
            <Input maxLength={120} placeholder="Juan Pérez, quiropráctico" />
          </Form.Item>
          <Form.Item name="tipo" label="Tipo">
            <Select options={[{ value: 'profesional', label: 'Profesional' }, { value: 'box', label: 'Box / sala' }]} />
          </Form.Item>
          <Form.Item name="recinto" label="Recinto" rules={[{ required: true, message: 'Elige el recinto' }]}>
            <Select placeholder="Selecciona un recinto" showSearch optionFilterProp="label"
              options={recintos.map(r => ({ value: r._id, label: formatearNombre(r.nombre) }))} />
          </Form.Item>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-x-4">
          <Form.Item name="servicios" label="Servicios que atiende">
            <Select mode="multiple" placeholder="Elige uno o más" optionFilterProp="label"
              options={servicios.map(s => ({ value: s._id, label: `${s.nombre}${s.activo ? '' : ' (inactivo)'}` }))} />
          </Form.Item>
          <Form.Item name="activo" label="Activa" valuePropName="checked">
            <Switch />
          </Form.Item>
        </div>
        <p className="text-[13px] font-semibold text-gray-700 mb-2">Horario semanal</p>
        <EditorHorario value={horario} onChange={setHorario} />
      </Form>
    </Modal>
  );
}

export default function Profesionales() {
  const notify = useNotify();
  const { modal } = App.useApp();
  const { user } = useAuthContext();
  const puedeEditar = can(user, 'agenda', 'editar');

  const [agendas, setAgendas] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [recintos, setRecintos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [vista, setVista] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [editando, setEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [bloqueando, setBloqueando] = useState(null); // agenda
  const [rango, setRango] = useState(null);
  const [porHoras, setPorHoras] = useState(false); // false = días completos (vacaciones)
  const [motivo, setMotivo] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [a, s] = await Promise.all([Agenda.agendas(), Agenda.servicios()]);
      setAgendas(a);
      setServicios(s);
      setBloqueando(b => (b ? a.find(x => x._id === b._id) || null : b));
    } catch (err) {
      notify.error(mensajeDeError(err, 'No se pudieron cargar los profesionales y boxes'));
    } finally {
      setCargando(false);
    }
  }, [notify]);

  useEffect(() => {
    cargar();
    Recintos.List({ limit: 1000 }).then(r => setRecintos((r.data || []).filter(x => x.estado === 'activo'))).catch(() => {});
  }, [cargar]);

  const conteo = useMemo(() => ({
    todos: agendas.length,
    activos: agendas.filter(a => a.activo).length,
    conBloqueos: agendas.filter(a => bloqueosVigentes(a).length).length,
  }), [agendas]);

  const visibles = agendas
    .filter(a => vista === 'todos' || (vista === 'activos' ? a.activo : bloqueosVigentes(a).length > 0))
    .filter(a => !busqueda || a.nombre.toLowerCase().includes(busqueda.toLowerCase()));

  const avisarAfectadas = (titulo, citas) => modal.warning({
    title: titulo,
    width: 560,
    content: <><p className="mb-0">Siguen en pie: reprográmalas desde Reservas.</p><ListaCitas citas={citas} /></>,
  });

  // ── Crear / editar ──
  const abrir = (a) => setEditando(a || {});
  const cerrar = () => setEditando(null);

  const cambiarEstado = async (a) => {
    try {
      await Agenda.actualizarAgenda(a._id, { activo: !a.activo });
      notify.success(a.activo ? 'Desactivado: deja de ofrecer horas' : 'Activado');
      cargar();
    } catch (err) {
      notify.error(mensajeDeError(err, 'No se pudo cambiar el estado'));
    }
  };

  // ── Bloqueos ──
  const bloquear = async (forzar = false) => {
    setGuardando(true);
    try {
      const [desde, hasta] = porHoras ? [comoHoraChile(rango[0]), comoHoraChile(rango[1])] : comoDiasChile(rango);
      const r = await Agenda.agregarBloqueo(bloqueando._id, { desde, hasta, motivo: motivo.trim() || undefined, forzar });
      setRango(null);
      setMotivo('');
      cargar();
      if (r.citasAfectadas?.length) avisarAfectadas('Bloqueo creado. Quedaron reservas dentro del tramo', r.citasAfectadas);
      else notify.success('Bloqueo creado');
    } catch (err) {
      const citas = err?.response?.data?.citas;
      if (err?.response?.data?.code === 'AGENDA_BLOQUEO_CON_CITAS' && citas?.length) {
        modal.confirm({
          title: `Hay ${citas.length} reserva${citas.length !== 1 ? 's' : ''} en ese tramo`,
          width: 560,
          content: <><p className="mb-0">Bloquear no las cancela ni las mueve: quedan en pie y hay que reprogramarlas (las que están retenidas o en pago siguen su curso).</p><ListaCitas citas={citas} /></>,
          okText: 'Bloquear igual', cancelText: 'Volver',
          onOk: () => bloquear(true),
        });
      } else {
        notify.error(mensajeDeError(err, 'No se pudo crear el bloqueo'));
      }
    } finally {
      setGuardando(false);
    }
  };

  const quitarBloqueo = (b) => modal.confirm({
    title: '¿Quitar el bloqueo?',
    content: 'Esas horas vuelven a ofrecerse en el sitio.',
    okText: 'Quitar', cancelText: 'Volver',
    onOk: async () => {
      try { await Agenda.quitarBloqueo(bloqueando._id, b._id); notify.success('Bloqueo quitado'); cargar(); }
      catch (err) { notify.error(mensajeDeError(err, 'No se pudo quitar')); }
    },
  });

  const columnas = [
    {
      title: 'Profesional o box', dataIndex: 'nombre', key: 'nombre',
      render: (n, a) => (
        <div className="flex items-center gap-3 min-w-0">
          <span className={`w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center
            ${a.activo ? 'bg-blue-50 text-primary' : 'bg-gray-100 text-gray-400'}`}>
            {a.tipo === 'box' ? <FiBox size={16} /> : <FiUser size={16} />}
          </span>
          <div className="min-w-0">
            <div className="font-bold text-gray-900 truncate">{n}</div>
            <div className="text-xs text-gray-500 truncate">{formatearNombre(a.recinto?.nombre) || 'Sin recinto'}</div>
          </div>
        </div>
      ),
    },
    { title: 'Tipo', dataIndex: 'tipo', key: 'tipo', width: 120, render: (t) => <ChipNeutro>{t === 'box' ? 'Box / sala' : 'Profesional'}</ChipNeutro> },
    {
      title: 'Servicios', dataIndex: 'servicios', key: 'servicios',
      render: (s = []) => s.length
        ? <span className="inline-flex flex-wrap gap-1.5">{s.map(x => <ChipNeutro key={x._id}>{x.nombre}</ChipNeutro>)}</span>
        : <span className="text-xs text-gray-400 italic">Ninguno</span>,
    },
    {
      title: 'Horario', dataIndex: 'disponibilidad', key: 'horario', width: 230,
      render: (d) => {
        const r = resumenHorario(d);
        return r.length
          ? <div className="text-xs text-gray-600 leading-5">{r.map(x => <div key={x} className="capitalize">{x}</div>)}</div>
          : <span className="text-xs text-[#92400e] font-semibold">Sin horario</span>;
      },
    },
    {
      title: 'Bloqueos', key: 'bloqueos', width: 120,
      render: (_, a) => {
        const v = bloqueosVigentes(a);
        return v.length
          ? <Tooltip title={v.map(b => `${textoBloqueo(b)}${b.motivo ? ` · ${b.motivo}` : ''}`).join('\n')}><span><Chip tono="amarillo">{v.length} vigente{v.length !== 1 ? 's' : ''}</Chip></span></Tooltip>
          : <span className="text-gray-300">—</span>;
      },
    },
    { title: 'Estado', dataIndex: 'activo', key: 'estado', width: 120, render: (a) => <ChipEstado activo={a} labelActivo="Activa" labelInactivo="Inactiva" /> },
    {
      title: <span className="sr-only">Acciones</span>, key: 'acciones', width: 64, align: 'center',
      render: (_, a) => (
        <span onClick={e => e.stopPropagation()}>
          <Dropdown trigger={['click']} placement="bottomRight" menu={{
            items: [
              { key: 'bloqueos', icon: <StopOutlined />, label: 'Bloqueos y vacaciones', onClick: () => setBloqueando(a) },
              ...(puedeEditar ? [
                { key: 'editar', icon: <EditOutlined />, label: 'Editar horario y servicios', onClick: () => abrir(a) },
                {
                  key: 'estado', icon: a.activo ? <CloseCircleOutlined /> : <CheckCircleOutlined />,
                  label: a.activo ? 'Desactivar' : 'Activar', onClick: () => cambiarEstado(a),
                },
              ] : []),
            ],
          }}>
            <button type="button" aria-label={`Acciones de ${a.nombre}`} className={claseBtnKebab}><FiMoreVertical size={17} /></button>
          </Dropdown>
        </span>
      ),
    },
  ];

  const vigentes = bloqueando ? bloqueosVigentes(bloqueando) : [];

  return (
    <div>
      <EncabezadoAdmin
        titulo="Profesionales y"
        destacado="boxes"
        subtitulo={`${conteo.todos} en total · ${conteo.activos} activos · ${conteo.conBloqueos} con bloqueos vigentes`}
      >
        {puedeEditar && (
          <button type="button" onClick={() => abrir(null)} className={claseBtnPrimario}>
            <FiPlus size={16} /> Nuevo profesional o box
          </button>
        )}
      </EncabezadoAdmin>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <TarjetaResumen icon={<FiLayers size={19} />} label="Todos" value={conteo.todos} activa={vista === 'todos'} onClick={() => setVista('todos')} />
        <TarjetaResumen icon={<FiCheckCircle size={19} />} label="Activos" value={conteo.activos} activa={vista === 'activos'} onClick={() => setVista('activos')} />
        <TarjetaResumen icon={<FiSlash size={19} />} label="Con bloqueos vigentes" value={conteo.conBloqueos} activa={vista === 'bloqueos'} onClick={() => setVista('bloqueos')} />
      </div>

      <BarraFiltros>
        <Input allowClear prefix={<FiSearch className="text-gray-400" />} placeholder="Buscar por nombre…"
          value={busqueda} onChange={(e) => setBusqueda(e.target.value)} className="!rounded-full" style={{ maxWidth: 340 }} aria-label="Buscar profesional o box" />
        <span className="ml-auto text-xs font-semibold text-gray-500">
          {visibles.length} resultado{visibles.length !== 1 ? 's' : ''}
        </span>
      </BarraFiltros>

      <div className="admin-table-card">
        <Table
          columns={columnas} dataSource={visibles} loading={cargando} rowKey="_id" sticky
          rowClassName={() => (puedeEditar ? 'fila-clicable' : '')}
          onRow={(a) => ({ onClick: () => puedeEditar && abrir(a) })}
          pagination={visibles.length > 20 ? { pageSize: 20, style: { padding: '12px 16px' } } : false}
          scroll={{ x: 1000 }}
          locale={{
            emptyText: agendas.length
              ? <Vacio titulo="Ninguno coincide" texto="Prueba con otra búsqueda o filtro." />
              : <Vacio titulo="Aún no hay profesionales ni boxes" texto="Crea el primero y asígnale servicios y horario." />,
          }}
        />
      </div>

      <details className="mt-5 rounded-2xl bg-white border border-gray-100 p-4">
        <summary className="cursor-pointer text-sm font-bold text-gray-700 list-none flex items-center gap-2">
          <FiInfo size={15} className="text-primary" /> Cómo funcionan el horario y los bloqueos
        </summary>
        <ul className="mt-3 mb-0 pl-5 space-y-1.5 text-sm text-gray-600">
          <li>Las horas se ofrecen dentro de cada tramo según la duración del servicio: 09:00–13:00 con un servicio de 45 min ofrece 09:00, 09:45, 10:30, 11:15 y 12:00.</li>
          <li>Un mismo profesional o box nunca recibe dos reservas que se pisen, aunque sean de servicios distintos.</li>
          <li>Cambiar el horario o bloquear días no cancela reservas ya pagadas: el panel avisa cuáles quedaron afectadas.</li>
        </ul>
      </details>

      {/* ── Crear / editar ── */}
      {editando !== null && (
        <ModalAgenda
          agenda={editando} recintos={recintos} servicios={servicios} onCerrar={cerrar}
          onGuardado={(advertencias) => {
            cerrar();
            cargar();
            if (advertencias?.length) avisarAfectadas('Guardado. Hay reservas fuera del nuevo horario', advertencias);
            else notify.success('Guardado');
          }}
        />
      )}

      {/* ── Bloqueos ── */}
      <Modal
        open={Boolean(bloqueando)} onCancel={() => { setBloqueando(null); setRango(null); setMotivo(''); }} width={620} destroyOnHidden
        title={<>Bloqueos de <span className="hl-amarillo">{bloqueando?.nombre}</span></>}
        footer={<button type="button" onClick={() => setBloqueando(null)} className={claseBtnSecundario}>Cerrar</button>}
      >
        {puedeEditar && (
          <div className="rounded-2xl bg-gray-50 border border-gray-100 p-3 mb-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <p className="text-[13px] font-semibold text-gray-700 m-0">Nuevo bloqueo (vacaciones, licencia, feriado)</p>
              <Segmented size="small" value={porHoras ? 'horas' : 'dias'}
                onChange={(v) => { setPorHoras(v === 'horas'); setRango(null); }}
                options={[{ value: 'dias', label: 'Días completos' }, { value: 'horas', label: 'Por horas' }]} />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {porHoras ? (
                <RangePicker key="horas" showTime={{ format: 'HH:mm', minuteStep: 15 }} format="DD/MM/YYYY HH:mm" value={rango} onChange={setRango}
                  placeholder={['Desde', 'Hasta']} />
              ) : (
                <RangePicker key="dias" format="DD/MM/YYYY" value={rango} onChange={setRango}
                  placeholder={['Primer día', 'Último día']} />
              )}
              <Input placeholder="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} style={{ width: 180 }} maxLength={200} />
              <button type="button" onClick={() => bloquear(false)} disabled={!rango || guardando} className={claseBtnPrimario}>
                {guardando ? <LoadingOutlined /> : <FiSlash size={15} />} Bloquear
              </button>
            </div>
            {!porHoras && (
              <p className="text-xs text-gray-500 mt-2 mb-0">
                El sitio no ofrece horas desde el primer día hasta el último, ambos incluidos. El resto de la semana sigue funcionando normal.
              </p>
            )}
          </div>
        )}
        {vigentes.length === 0 ? (
          <Vacio titulo="Sin bloqueos vigentes" texto="Todas sus horas se ofrecen según el horario semanal." />
        ) : (
          <ul className="m-0 p-0 list-none divide-y divide-gray-100">
            {vigentes.map(b => (
              <li key={b._id} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 m-0 tabular-nums">{textoBloqueo(b)}</p>
                  {b.motivo && <p className="text-xs text-gray-500 m-0">{b.motivo}</p>}
                </div>
                {puedeEditar && (
                  <button type="button" onClick={() => quitarBloqueo(b)} aria-label="Quitar bloqueo"
                    className="w-9 h-9 inline-flex items-center justify-center rounded-full text-gray-400 hover:bg-red-50 hover:text-red-600">
                    <FiTrash2 size={15} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </div>
  );
}
